import type { ReactNode } from "react";
import { SWRConfig } from "swr";
import { timeStrToMinutes } from "../lib/date-utils";
import { errorToString } from "../lib/error-utils";
import type { TypedUrl } from "./common";
// swr's `mutate`, wrapped so invalidation reaches unmounted readers too.
import { mutate } from "./mutate";

const processBody = (data: any): any => {
  if (data) {
    if (Array.isArray(data)) data.forEach(processBody);
    if (data.createdAt) data.createdAt = new Date(data.createdAt);
    if (data.createdAt)
      data.lastEditedAt = data.lastEditedAt && new Date(data.lastEditedAt);
    if (data.startAt) data.startAt = new Date(data.startAt);
    if (data.expiresAt) data.expiresAt = new Date(data.expiresAt);
    if (data.endsAt) data.endsAt = new Date(data.endsAt);
    if (data.lastActionAt) data.lastActionAt = new Date(data.lastActionAt);
    if (data.archivedAt) data.archivedAt = new Date(data.archivedAt);
    if (data.participants) data.participants.forEach(processBody);
    if (data.comments) data.comments.forEach(processBody);
    if (data.schedule) data.schedule.forEach(processBody);
    if (data.startTime) data.startTime = timeStrToMinutes(data.startTime);
  }
  return data;
};

type FetcherOpts = {
  headers?: Record<string, string>;
  method?: string;
  body?: string;
};

const fetcher =
  (opts: FetcherOpts = {}) =>
  async (url: string): Promise<any> => {
    const headers = { ...opts.headers };
    const response = await window.fetch(
      `${import.meta.env.VITE_API_HOST}${url}`,
      {
        mode: "cors",
        credentials: "include",
        ...opts,
        headers,
      },
    );

    if (response.status === 304) {
      return undefined; // Use cached result
    }

    if (response.status === 401) {
      return null;
    }

    if (response.status >= 400) {
      let body: { message?: string } | undefined;
      try {
        body = await response.json();
      } catch {
        /* ignore */
      }
      const error = new Error(
        errorToString(body?.message ?? response.statusText),
      ) as Error & { status: number };
      error.status = response.status;
      throw error;
    }

    if (response.ok) {
      const body = await response.json();
      return processBody(body);
    }
  };

// The bound fetcher used by SWR (wired in via `swrOptions` below).
export const defaultFetcher = fetcher();

export type MutateKey =
  | TypedUrl<unknown>
  | string
  | ((key: unknown) => boolean)
  | null
  | undefined;

export function post<T = any>(
  url: string,
  data: unknown,
  mutateModels: ReadonlyArray<MutateKey> = [],
): Promise<T> {
  return fetcher({
    headers: { "Content-Type": "application/json" },
    method: "post",
    body: JSON.stringify(data),
  })(url).then((res) => {
    mutateModels.forEach((key) => {
      if (key) void mutate(key);
    });
    return res as T;
  });
}

export const clearAllCaches = () =>
  mutate(() => true, undefined, { revalidate: false });

const swrOptions = {
  fetcher: defaultFetcher,
  onErrorRetry: (error: { status?: number }) => {
    if (error.status && [304, 401, 403, 404].includes(error.status)) return;
  },
};

export const ApiProvider = ({ children }: { children: ReactNode }) => (
  <SWRConfig value={swrOptions}>{children}</SWRConfig>
);
